import * as React from "react";

import { ComicPanel } from "@/components/comic";
import { PageTitle } from "@/components/PageTitle";
import { GuestProvider, guestProviders } from "@/features/marketplace/providers";

import { ConversationPanel } from "./components/ConversationPanel";

/**
 * The chat page as an outsider first meets it: rendered in full, and inert.
 *
 * <p>Exposing it publicly is for enticement, so this is the real page rather than a picture of
 * one. Nothing behind it runs. There is <b>no network call of any kind</b> before an identity is
 * verified (D-37), which is why this file imports no API client, no session hook and no data
 * fetch, and why {@code SignedOutChatPageIsInertTest} renders it with no providers at all and
 * fails if anything reaches for {@code fetch}.</p>
 *
 * <p>There is also <b>no sample transcript and no draft in the composer</b>. An earlier mock put a
 * worked example in the thread and it read as a message the visitor had written, which is the one
 * thing a page that cannot yet be used must not suggest. The thread shows its ordinary empty
 * state.</p>
 *
 * <p>Two columns, not three. The transmission rail is a member surface (D-38) and means nothing to
 * someone with no history; it is one of the things creating an account hands over. The left rail
 * holds identities rather than a workspace (D-45): calendars and platform connections are
 * host-side concepts, and a visitor has neither.</p>
 */
export function SignedOutChatPage({
  /** What this deployment can actually complete a sign-in with. Injected so it is testable. */
  providers = guestProviders(),
}: {
  providers?: GuestProvider[];
} = {}): React.ReactElement {
  return (
    <div className="mx-auto max-w-[1140px] space-y-4 px-4 py-6 font-comic-body">
      <header>
        <PageTitle>Schedule through your agent</PageTitle>
        <p className="text-xs text-muted-foreground">
          Sign in to ask. Your agent handles the back and forth.
        </p>
      </header>

      <div className="grid items-start gap-4 lg:grid-cols-[240px_minmax(0,1fr)]">
        <aside className="space-y-4">
          <span className="px-1 font-display text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Your identities
          </span>
          <ComicPanel title="Sign in">
            <div className="space-y-2">
              {providers.map((provider) => (
                <ProviderButton key={provider.id} provider={provider} />
              ))}
            </div>
            {/*
              Before the buttons do anything, not after (D-84). Every provider we broker requires
              the visitor to be told what is collected and how to get it removed BEFORE they
              authenticate — LinkedIn's API terms say so in as many words, and the GDPR route to the
              same place is Art. 13. A privacy policy reachable only from inside the app is no use
              to somebody who has not signed into anything.

              A plain link, like the buttons above it: this page still calls nothing (D-37).
            */}
            <p className="mt-3 text-[11px] text-muted-foreground">
              We ask your provider for your name and email, never your calendar.{" "}
              <a href="/privacy" className="underline hover:text-foreground">
                What we keep, and how to have it removed
              </a>
              .
            </p>
          </ComicPanel>
        </aside>

        <div className="min-w-0">
          <ConversationPanel
            mode="outgoing"
            messages={[]}
            draft=""
            onDraftChange={NOOP}
            onSubmit={PREVENT}
            busy={false}
            locked
            emptyHint={
              <span>
                No messages yet. Sign in to ask for a meeting, and your agent handles the
                back-and-forth.
              </span>
            }
          />
        </div>
      </div>
    </div>
  );
}

/**
 * Identity only: openid, email, profile. Never a calendar scope — those are Sensitive and would
 * restart the Google verification granted on 2026-08-20 (D-33). Connecting a calendar is a
 * separate flow with a separate client.
 *
 * <p>A plain link to the broker, deliberately: a full-page navigation is what an OAuth redirect
 * needs, and it keeps this page's promise that nothing runs before an identity is proved. Clicking
 * one leaves the page rather than making a call from it (D-37).</p>
 *
 * <p>Only the providers this deployment actually brokers are offered. One that is not configured
 * has no sign-in path at all — the link would 404 on click, which is a worse first impression than
 * a shorter list (D-85).</p>
 */
function ProviderButton({ provider }: { provider: GuestProvider }): React.ReactElement {
  return (
    <a
      href={`/api/v1/marketplace/signin/${provider.id}`}
      className="flex w-full items-center gap-2 rounded-xl border border-border bg-card px-3 py-2 text-sm font-medium text-foreground transition hover:-translate-y-px hover:border-brand-blue"
    >
      {provider.label}
    </a>
  );
}

const NOOP = (): void => {};
const PREVENT = (e: React.FormEvent): void => e.preventDefault();
